import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import RotatingHeadline from "@/components/RotatingHeadline";
import HeroSection from "@/components/HeroSection";
import UDAFeatureAccordion from "@/components/UDAFeatureAccordion";
import HomepageFAQ from "@/components/HomepageFAQ";
import CanonicalTag from "@/components/CanonicalTag";
import { ModelBox } from "@/components/ModelBox";
import { NeumorphicThemeProvider } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import VisitorTracker from "@/components/VisitorTracker";
import ADUWizard from "@/components/what-fits-my-lot/wizard/ADUWizard";
import ReviewCarousel from "@/components/ReviewCarousel";
import { 
  NeumorphicContainer, 
  NeumorphicCard, 
  NeumorphicButton,
  NeumorphicVideoBox,
  NeumorphicCircle,
  NeumorphicModelImage
} from "@/components/neumorphic";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";

const IndexContent = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const { theme } = useNeumorphicTheme();

  // Exclusive models from UDA Models page
  const exclusiveModels = [{
    id: 1,
    name: "Gilmour",
    squareFootage: 378,
    beds: 0,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0910924-d4cb-4b53-8131-e7364b762518.png"],
      "3D": ["/lovable-uploads/0114c63b-5361-41d4-8633-feac775819c0.png"],
      exterior: ["/lovable-uploads/415954b9-1a79-470f-bf8b-3f5981dc17da.png"],
      interior: []
    }
  }, {
    id: 2,
    name: "Di Meola",
    squareFootage: 389,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0402f4be-93a9-4e6f-83c9-f390ea2aa5b6.png"],
      "3D": ["/lovable-uploads/045c9713-fc7d-47ab-8f98-5ee5bdd0aeb5.png"],
      exterior: ["/lovable-uploads/09797886-664e-4aa9-b648-d23241116ddd.png"],
      interior: []
    }
  }, {
    id: 3,
    name: "Satriani",
    squareFootage: 541,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/07742ba5-337c-4155-8432-cece1acdbe88.png"],
      "3D": ["/lovable-uploads/07dfc299-225b-4d3c-b4c3-7ae077c4b1ee.png"],
      exterior: ["/lovable-uploads/f55e0610-bfdd-4421-a78d-6077b3d8a540.png"],
      interior: []
    }
  }, {
    id: 4,
    name: "Knopfler",
    squareFootage: 797,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0a60f49b-a0b6-4dd4-9257-d06f9428f393.png"],
      "3D": ["/lovable-uploads/0b8f05f0-c562-4dc2-906f-07f942e98dc1.png"],
      exterior: ["/lovable-uploads/81450f32-921a-4836-9e5f-f88339547387.png"],
      interior: []
    }
  }, {
    id: 5,
    name: "Moore",
    squareFootage: 815,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/0fb32522-99b0-4ad7-8f82-6664dad62226.png"],
      "3D": ["/lovable-uploads/104e2d5e-2f29-44da-859e-968bce9636e9.png"],
      exterior: ["/lovable-uploads/37873af0-0fc2-4ca9-8ea0-1049dd49a3de.png"],
      interior: []
    }
  }];

  // Los Altos models to replace existing floor plans
  const losAltosModels = [{
    id: 13,
    name: "LA Wee",
    squareFootage: 525,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506005&miniplan=original",
    images: {
      "2D": ["/lovable-uploads/fe69ec00-a304-485a-9099-cab81cd51b2e.png"],
      "3D": ["/lovable-uploads/fe69ec00-a304-485a-9099-cab81cd51b2e.png"],
      exterior: ["/lovable-uploads/c129b40b-8432-491f-a1af-a47d817aeee4.png"],
      interior: []
    }
  }, {
    id: 14,
    name: "LA Median",
    squareFootage: 854,
    beds: 1,
    baths: 1,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506008&miniplan=original",
    images: {
      "2D": ["/lovable-uploads/1399ed1c-d18a-411f-9862-b927eb39dca5.png"],
      "3D": ["/lovable-uploads/1399ed1c-d18a-411f-9862-b927eb39dca5.png"],
      exterior: ["/lovable-uploads/b616a741-04ec-4dfd-82b8-6311f8a5d3b8.png"],
      interior: []
    }
  }, {
    id: 15,
    name: "LA Grand",
    squareFootage: 1190,
    beds: 2,
    baths: 2,
    widgetUrl: "https://udaadu.widget.floor-plan.online/beta/?crmPlanId=506009",
    images: {
      "2D": ["/lovable-uploads/f880f594-ac6d-418c-992d-649410b53e13.png"],
      "3D": ["/lovable-uploads/f880f594-ac6d-418c-992d-649410b53e13.png"],
      exterior: ["/lovable-uploads/1125af17-6a9b-45a5-8c3a-f3a53e6daccd.png"],
      interior: []
    }
  }];

  const openCalendlyModal = () => {
    setShowCalendly(true);
  };
  const closeCalendlyModal = () => {
    setShowCalendly(false);
  };

  useEffect(() => {
    // Add FAQPage schema for homepage
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [{
        "@type": "Question",
        "name": "How much does it cost to build an ADU?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "UDA ADU projects typically range from $150,000 and UP depending on size, layout, and site conditions. Use our ADU Calculator for a free estimate."
        }
      }, {
        "@type": "Question",
        "name": "Can I rent out my ADU for income?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes — most UDA ADUs earn between $2,500 and $7,000 per month in rental income depending on location and size."
        }
      }, {
        "@type": "Question",
        "name": "Do you offer custom ADUs or only pre-approved plans?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We offer both: fully custom ADU designs tailored to your lot, and pre-approved plans for faster builds with guaranteed zoning compliance."
        }
      }]
    });
    document.head.appendChild(faqSchema);

    // Cleanup function to remove scripts when component unmounts
    return () => {
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      const existingFaqSchema = document.querySelector('script[type="application/ld+json"]:last-of-type');
      if (existingFaqSchema && existingFaqSchema.innerHTML.includes('FAQPage')) {
        existingFaqSchema.remove();
      }
    };
  }, []);

  const textColor = theme === "dark" ? "text-white" : "text-[#1C2333]";
  const mutedTextColor = theme === "dark" ? "text-gray-300" : "text-[#4a4a4a]";

  return (
    <NeumorphicContainer>
      <CanonicalTag url="https://www.udaadu.com/" />
      <Navigation />

      {/* Rotating Headlines Component */}
      <RotatingHeadline />

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <NeumorphicCard className="relative w-full max-w-2xl h-[70vh] overflow-hidden p-2">
            <button 
              onClick={closeCalendlyModal} 
              className={`absolute top-4 right-4 z-10 p-2 rounded-full shadow-md transition-colors ${
                theme === "dark" 
                  ? "bg-[#1C2333] hover:bg-[#242d44] text-white" 
                  : "bg-[#FFE4BF] hover:bg-[#f5d9ab] text-[#1C2333]"
              }`}
            >
              <X className="h-6 w-6" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
              className="rounded-xl"
            />
          </NeumorphicCard>
        </div>
      )}

      {/* Main Content Area */}
      <main className="md:max-w-7xl md:mx-auto md:px-4 py-0">
        <div className="text-center my-0 py-0">
          {/* Video Section - Mobile: reduced height to show full video, Desktop: unchanged */}
          <div className="w-full max-w-6xl md:mx-auto px-2 md:px-4 mt-0 mb-4 md:my-[8px] my-[3px] py-0">
            <NeumorphicVideoBox>
              <video 
                className="w-full h-[250px] md:h-auto object-contain md:object-contain" 
                autoPlay 
                loop 
                muted 
                playsInline 
                onContextMenu={e => e.preventDefault()} 
                style={{ pointerEvents: 'none' }}
              >
                <source 
                  src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/udavideos/7-23%20UDA%20ADU%20MAIN%20PAGE%20.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJ1ZGF2aWRlb3MvNy0yMyBVREEgQURVIE1BSU4gUEFHRSAubXA0IiwiaWF0IjoxNzUzMzAyMTU1LCJleHAiOjIwNjg2NjIxNTV9.zOMy6yIjH_cr1YmJhRFvvsfZsO_n9zEADkXkf6noxxY" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </NeumorphicVideoBox>
          </div>

          {/* ADU Builder Section */}
          <div className="w-full max-w-4xl md:max-w-5xl md:mx-auto px-4 mt-3 mb-8 md:mt-10 md:mb-8 py-0 my-0">
            <NeumorphicCard className="p-6 md:p-10">
              <h2 className="text-[1.4rem] py-[7px] text-[#e4683b] md:text-4xl text-center font-normal">
                Top-Rated Accessory Dwelling Unit (ADU) Builder in the Bay Area, California — Guaranteed Permits, Smart Design, and Maximum ROI
              </h2>
            </NeumorphicCard>
          </div>

          {/* ADU Wizard Section */}
          <div className="w-full max-w-4xl mx-auto px-4 my-4 md:my-12">
            <div className="text-center mb-3 md:mb-6">
              <h2 className="mb-1 md:mb-2 text-2xl md:text-4xl font-normal" style={{ color: "#E4683B" }}>
                What Fits Your Lot?
              </h2>
              <p className={`md:text-lg ${mutedTextColor} text-lg`}>
                Discover your property's ADU potential in minutes
              </p>
            </div>
            <ADUWizard isDark={theme === "dark"} compact />
          </div>

          {/* Video Section */}
          <div className="w-full max-w-4xl mx-auto px-4 py-[15px] my-0">
            <NeumorphicVideoBox>
              <video className="w-full h-auto" autoPlay loop muted playsInline>
                <source 
                  src="https://wrghlmsinvhwvvfhhrvj.supabase.co/storage/v1/object/sign/VIDEOS/Grey%20White%20Film%20Journal%20Animated%20Video%20Intro.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8zMWY3NTE2YS1mMTNjLTQ2MzEtYTliMC1mOWJmMGFjNTBkZjMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJWSURFT1MvR3JleSBXaGl0ZSBGaWxtIEpvdXJuYWwgQW5pbWF0ZWQgVmlkZW8gSW50cm8ubXA0IiwiaWF0IjoxNzUyMjc1NTA2LCJleHAiOjIwNjc2MzU1MDZ9.lfa6uSn54QV4QghXYc8YRJ648LZ826iOdx4CgfMZd_o" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </NeumorphicVideoBox>
          </div>

          {/* Custom ADU Discussion Call to Action */}
          <div className="w-full max-w-4xl mx-auto px-4 text-center py-[34px] my-[48px]">
            <NeumorphicCard className="p-8 md:p-12">
              <h2 className={`md:text-5xl mb-12 text-center font-light lg:text-3xl ${textColor} text-2xl`}>
                <span className="hidden md:inline text-4xl font-normal">Custom | Pre-Approved | Pre-Designed</span>
                <span className="md:hidden">Custom<br />Pre-Approved<br />Pre-Designed</span>
              </h2>
              <h3 className="text-brand mb-8 text-3xl md:text-4xl font-normal">
                Are you ready to discuss your custom ADU?
              </h3>
              <NeumorphicButton onClick={openCalendlyModal} size="lg">
                Book Your Consultation
              </NeumorphicButton>
            </NeumorphicCard>
          </div>

          {/* Los Altos Preapproved and Permit Ready Plans - No container, raised model images on main background */}
          <div className="w-full max-w-6xl mx-auto px-4 my-[35px] py-[10px]">
            <h3 className="md:text-4xl text-brand mb-8 text-center text-3xl font-normal">
              Los Altos Preapproved and Permit Ready Plans
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {losAltosModels.map(model => (
                <div key={model.id} className="rounded-2xl overflow-hidden">
                  <ModelBox model={model} />
                </div>
              ))}
            </div>
          </div>

          {/* Our Exclusive Pre-designed Models - Flat container with raised model images */}
          <div className="w-full max-w-6xl mx-auto px-4 my-[7px] py-[11px]">
            <NeumorphicCard className="p-6 md:p-8" variant="flat">
              <h3 className="md:text-4xl text-brand mb-8 text-center text-3xl font-normal">
                Our Exclusive Pre-designed Models
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
                {exclusiveModels.map(model => (
                  <NeumorphicModelImage key={model.id}>
                    <ModelBox model={model} />
                  </NeumorphicModelImage>
                ))}
              </div>
            </NeumorphicCard>
          </div>

          {/* UDA Feature Accordion */}
          <div className="w-full max-w-6xl mx-auto px-4 my-8">
            <NeumorphicCard className="p-6 md:p-8">
              <UDAFeatureAccordion />
            </NeumorphicCard>
          </div>

          {/* Hero Section */}
          <div className="w-full max-w-6xl mx-auto px-4 my-8">
            <NeumorphicCard className="overflow-hidden">
              <HeroSection />
            </NeumorphicCard>
          </div>
        </div>
      </main>

      {/* Homepage FAQ Section - positioned at bottom above footer */}
      <div className="w-full max-w-6xl mx-auto px-4 my-8">
        <NeumorphicCard className="p-6 md:p-8">
          <HomepageFAQ />
        </NeumorphicCard>
      </div>

      <ReviewCarousel isDark={theme === "dark"} />
      
      <Footer />
      <NeumorphicThemeToggle />
    </NeumorphicContainer>
  );
};

const Index = () => {
  return (
    <NeumorphicThemeProvider>
      <VisitorTracker />
      <IndexContent />
    </NeumorphicThemeProvider>
  );
};

export default Index;
